import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRight,
  faArrowRotateRight,
  faBan,
  faBell,
  faBolt,
  faCheck,
  faCirclePause,
  faCirclePlay,
  faClock,
  faEnvelope,
  faMobileScreen,
  faPaperPlane,
  faPen,
  faTriangleExclamation,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  notificationCampaignsApi,
} from "../api/notificationCampaignsApi";

import type {
  CampaignDeliveryLog,
  CampaignExecution,
  CampaignRecipient,
  CampaignSummary,
  NotificationCampaign,
} from "../types/notification-campaigns.types";

import "../screenstyles/NotificationCampaignDetails.css";

function formatTimestamp(
  value: any,
) {
  if (!value) {
    return "—";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",
      month:
        "short",
      day:
        "2-digit",
      hour:
        "2-digit",
      minute:
        "2-digit",
    },
  ).format(date);
}

function statusLabel(
  status: string,
) {
  const labels:
    Record<
      string,
      string
    > = {
      draft:
        "مسودة",

      scheduled:
        "مجدولة",

      sending:
        "جاري الإرسال",

      sent:
        "تم الإرسال",

      paused:
        "متوقفة",

      failed:
        "فشلت",

      cancelled:
        "ملغاة",

      queued:
        "في الانتظار",

      running:
        "قيد التنفيذ",

      retrying:
        "إعادة محاولة",

      completed:
        "مكتملة",

      dead_letter:
        "Dead Letter",

      processing:
        "قيد المعالجة",

      skipped:
        "تم التخطي",
    };

  return (
    labels[
      status
    ] ??
    status
  );
}

const NotificationCampaignDetails:
React.FC = () => {
  const {
    campaignId,
  } =
    useParams<{
      campaignId:
        string;
    }>();

  const navigate =
    useNavigate();

  const [
    campaign,
    setCampaign,
  ] =
    useState<
      NotificationCampaign |
      null
    >(null);

  const [
    summary,
    setSummary,
  ] =
    useState<
      CampaignSummary |
      null
    >(null);

  const [
    executions,
    setExecutions,
  ] =
    useState<
      CampaignExecution[]
    >([]);

  const [
    selectedExecution,
    setSelectedExecution,
  ] =
    useState<
      CampaignExecution |
      null
    >(null);

  const [
    recipients,
    setRecipients,
  ] =
    useState<
      CampaignRecipient[]
    >([]);

  const [
    deliveryLogs,
    setDeliveryLogs,
  ] =
    useState<
      CampaignDeliveryLog[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    reportLoading,
    setReportLoading,
  ] =
    useState(false);

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(false);

  const loadCampaign =
    useCallback(
      async () => {
        if (
          !campaignId
        ) {
          return;
        }

        try {
          setLoading(
            true,
          );

          const [
            campaignResult,
            summaryResult,
            executionsResult,
          ] =
            await Promise.all([
              notificationCampaignsApi
                .getById(
                  campaignId,
                ),

              notificationCampaignsApi
                .getSummary(
                  campaignId,
                ),

              notificationCampaignsApi
                .getExecutions(
                  campaignId,
                  {
                    limit:
                      100,
                  },
                ),
            ]);

          setCampaign(
            campaignResult
              .campaign,
          );

          setSummary(
            summaryResult,
          );

          setExecutions(
            executionsResult
              .items ??
              [],
          );
        } catch (
          error: any
        ) {
          console.error(
            "Failed to load campaign details:",
            error,
          );

          toast.error(
            error?.message ||
              "فشل تحميل تفاصيل الحملة",
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        campaignId,
      ],
    );

  useEffect(() => {
    if (
      !campaignId
    ) {
      navigate(
        "/notification-campaigns",
      );

      return;
    }

    loadCampaign();
  }, [
    campaignId,
    navigate,
    loadCampaign,
  ]);

  const loadExecutionReports =
    async (
      execution:
        CampaignExecution,
    ) => {
      if (
        !campaignId
      ) {
        return;
      }

      try {
        setReportLoading(
          true,
        );

        setSelectedExecution(
          execution,
        );

        const [
          recipientResult,
          logsResult,
        ] =
          await Promise.all([
            notificationCampaignsApi
              .getRecipients(
                campaignId,
                execution.executionId,
                {
                  limit:
                    100,
                },
              ),

            notificationCampaignsApi
              .getDeliveryLogs(
                campaignId,
                execution.executionId,
                {
                  limit:
                    100,
                },
              ),
          ]);

        setRecipients(
          recipientResult
            .items ??
            [],
        );

        setDeliveryLogs(
          logsResult
            .items ??
            [],
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to load execution reports:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تحميل تقارير التنفيذ",
        );
      } finally {
        setReportLoading(
          false,
        );
      }
    };

  const performAction =
    async (
      action:
        | "send"
        | "pause"
        | "resume"
        | "cancel",
    ) => {
      if (
        !campaignId ||
        !campaign
      ) {
        return;
      }

      try {
        setActionLoading(
          true,
        );

        if (
          action ===
          "send"
        ) {
          if (
            !window.confirm(
              "هل تريد إرسال هذه الحملة الآن؟",
            )
          ) {
            return;
          }

          const result =
            await notificationCampaignsApi
              .sendNow(
                campaignId,
              );

          toast.success(
            `تم وضع التنفيذ في قائمة الإرسال: ${result.executionId}`,
          );
        }

        if (
          action ===
          "pause"
        ) {
          if (
            !window.confirm(
              "هل تريد إيقاف الحملة مؤقتًا؟",
            )
          ) {
            return;
          }

          await notificationCampaignsApi
            .pause(
              campaignId,
            );

          toast.success(
            "تم إيقاف الحملة",
          );
        }

        if (
          action ===
          "resume"
        ) {
          await notificationCampaignsApi
            .resume(
              campaignId,
            );

          toast.success(
            "تم استئناف الحملة",
          );
        }

        if (
          action ===
          "cancel"
        ) {
          if (
            !window.confirm(
              "هل أنت متأكد من إلغاء الحملة؟",
            )
          ) {
            return;
          }

          await notificationCampaignsApi
            .cancel(
              campaignId,
            );

          toast.success(
            "تم إلغاء الحملة",
          );
        }

        await loadCampaign();
      } catch (
        error: any
      ) {
        console.error(
          "Campaign action failed:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تنفيذ العملية",
        );
      } finally {
        setActionLoading(
          false,
        );
      }
    };

  if (
    loading
  ) {
    return (
      <div className="notification-campaign-details-page">
        <div className="notification-campaign-details-state">
          جاري تحميل تفاصيل الحملة...
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="notification-campaign-details-page">
        <div className="notification-campaign-details-state">
          الحملة غير موجودة.
        </div>
      </div>
    );
  }

  return (
    <div className="notification-campaign-details-page">
      <header className="notification-campaign-details-header">
        <div>
          <span className="notification-campaign-details-eyebrow">
            تفاصيل حملة الإشعارات
          </span>

          <h1>
            {campaign.name}
          </h1>

          <p>
            {campaign.title}
          </p>
        </div>

        <div className="notification-campaign-details-header-actions">
          <button
            type="button"
            onClick={() =>
              loadCampaign()
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowRotateRight
              }
            />

            تحديث
          </button>

          {campaign.status !==
            "sent" &&
            campaign.status !==
              "cancelled" && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/notification-campaigns/${campaignId}/edit`,
                  )
                }
              >
                <FontAwesomeIcon
                  icon={faPen}
                />

                تعديل
              </button>
            )}

          <button
            type="button"
            onClick={() =>
              navigate(
                "/notification-campaigns",
              )
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowRight
              }
            />

            رجوع
          </button>
        </div>
      </header>

      <section className="notification-campaign-details-actions">
        {campaign.status !==
          "sent" &&
          campaign.status !==
            "cancelled" &&
          campaign.status !==
            "sending" && (
            <button
              type="button"
              className="send"
              disabled={
                actionLoading
              }
              onClick={() =>
                performAction(
                  "send",
                )
              }
            >
              <FontAwesomeIcon
                icon={
                  faPaperPlane
                }
              />

              إرسال الآن
            </button>
          )}

        {campaign.status ===
        "paused" ? (
          <button
            type="button"
            className="resume"
            disabled={
              actionLoading
            }
            onClick={() =>
              performAction(
                "resume",
              )
            }
          >
            <FontAwesomeIcon
              icon={
                faCirclePlay
              }
            />

            استئناف
          </button>
        ) : (
          campaign.status !==
            "sent" &&
          campaign.status !==
            "cancelled" &&
          campaign.status !==
            "failed" && (
            <button
              type="button"
              className="pause"
              disabled={
                actionLoading
              }
              onClick={() =>
                performAction(
                  "pause",
                )
              }
            >
              <FontAwesomeIcon
                icon={
                  faCirclePause
                }
              />

              إيقاف مؤقت
            </button>
          )
        )}

        {campaign.status !==
          "sent" &&
          campaign.status !==
            "cancelled" && (
            <button
              type="button"
              className="cancel"
              disabled={
                actionLoading
              }
              onClick={() =>
                performAction(
                  "cancel",
                )
              }
            >
              <FontAwesomeIcon
                icon={faBan}
              />

              إلغاء الحملة
            </button>
          )}
      </section>

      <section className="notification-campaign-details-grid">
        <div className="notification-campaign-details-card main-info">
          <div className="notification-campaign-details-title">
            <FontAwesomeIcon
              icon={faBell}
            />

            <h2>
              معلومات الحملة
            </h2>
          </div>

          <div className="notification-campaign-info-grid">
            <div>
              <span>
                الحالة
              </span>

              <strong>
                {statusLabel(
                  campaign.status,
                )}
              </strong>
            </div>

            <div>
              <span>
                النوع
              </span>

              <strong>
                {campaign.type}
              </strong>
            </div>

            <div>
              <span>
                طريقة الإرسال
              </span>

              <strong>
                {campaign.sendMode}
              </strong>
            </div>

            <div>
              <span>
                الأولوية
              </span>

              <strong>
                {campaign.priority}
              </strong>
            </div>

            <div>
              <span>
                مجدولة في
              </span>

              <strong>
                {formatTimestamp(
                  campaign.scheduledAt,
                )}
              </strong>
            </div>

            <div>
              <span>
                آخر تشغيل
              </span>

              <strong>
                {formatTimestamp(
                  campaign.lastRunAt,
                )}
              </strong>
            </div>

            <div>
              <span>
                التشغيل القادم
              </span>

              <strong>
                {formatTimestamp(
                  campaign.nextRunAt,
                )}
              </strong>
            </div>

            <div>
              <span>
                أنشئت بواسطة
              </span>

              <strong>
                {campaign.createdByEmail ||
                  campaign.createdBy ||
                  "—"}
              </strong>
            </div>
          </div>

          <div className="notification-campaign-message-box">
            <strong>
              {campaign.title}
            </strong>

            <p>
              {campaign.body}
            </p>

            {campaign.imageUrl && (
              <img
                src={
                  campaign.imageUrl
                }
                alt=""
              />
            )}
          </div>
        </div>

        <div className="notification-campaign-details-card">
          <div className="notification-campaign-details-title">
            <FontAwesomeIcon
              icon={faUsers}
            />

            <h2>
              الجمهور
            </h2>
          </div>

          <div className="notification-campaign-tags">
            {campaign.target.accountTypes.map(
              value => (
                <span
                  key={
                    value
                  }
                >
                  {value}
                </span>
              ),
            )}

            {campaign.target.accountLevels.map(
              value => (
                <span
                  key={
                    value
                  }
                >
                  {value}
                </span>
              ),
            )}

            {campaign.target.languages.map(
              value => (
                <span
                  key={
                    value
                  }
                >
                  {value}
                </span>
              ),
            )}

            {campaign.target.platforms.map(
              value => (
                <span
                  key={
                    value
                  }
                >
                  {value}
                </span>
              ),
            )}

            {campaign.target.provinces.map(
              value => (
                <span
                  key={
                    value
                  }
                >
                  {value}
                </span>
              ),
            )}

            {campaign.target.userIds.length ===
              0 &&
              campaign.target.accountTypes.length ===
                0 &&
              campaign.target.accountLevels.length ===
                0 &&
              campaign.target.languages.length ===
                0 &&
              campaign.target.platforms.length ===
                0 &&
              campaign.target.provinces.length ===
                0 && (
                <p>
                  لا توجد قيود استهداف
                  محددة.
                </p>
              )}
          </div>
        </div>
      </section>

      {summary && (
        <section className="notification-campaign-summary-grid">
          <div>
            <FontAwesomeIcon
              icon={faClock}
            />

            <span>
              التنفيذات
            </span>

            <strong>
              {
                summary.executionsCount
              }
            </strong>
          </div>

          <div>
            <FontAwesomeIcon
              icon={faUsers}
            />

            <span>
              المستخدمون المطابقون
            </span>

            <strong>
              {
                summary.totals
                  .matchedUsers
              }
            </strong>
          </div>

          <div>
            <FontAwesomeIcon
              icon={faCheck}
            />

            <span>
              تم الإرسال
            </span>

            <strong>
              {
                summary.totals
                  .pushSuccessCount
              }
            </strong>
          </div>

          <div>
            <FontAwesomeIcon
              icon={
                faTriangleExclamation
              }
            />

            <span>
              فشل الإرسال
            </span>

            <strong>
              {
                summary.totals
                  .pushFailureCount
              }
            </strong>
          </div>
        </section>
      )}

      <section className="notification-campaign-details-card notification-campaign-executions-card">
        <div className="notification-campaign-details-title">
          <FontAwesomeIcon
            icon={faBolt}
          />

          <div>
            <h2>
              عمليات التنفيذ
            </h2>

            <p>
              اختر عملية لرؤية
              المستلمين وسجل الإرسال.
            </p>
          </div>
        </div>

        {executions.length ===
        0 ? (
          <div className="notification-campaign-details-empty">
            لم يتم تنفيذ الحملة حتى الآن.
          </div>
        ) : (
          <div className="notification-details-table-wrapper">
            <table className="notification-details-table">
              <thead>
                <tr>
                  <th>
                    Execution ID
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    النوع
                  </th>

                  <th>
                    المطابقون
                  </th>

                  <th>
                    أرسل
                  </th>

                  <th>
                    فشل
                  </th>

                  <th>
                    المحاولات
                  </th>

                  <th>
                    البداية
                  </th>
                </tr>
              </thead>

              <tbody>
                {executions.map(
                  execution => (
                    <tr
                      key={
                        execution.executionId
                      }
                      className={
                        selectedExecution
                          ?.executionId ===
                        execution.executionId
                          ? "selected"
                          : ""
                      }
                      onClick={() =>
                        loadExecutionReports(
                          execution,
                        )
                      }
                    >
                      <td dir="ltr">
                        {
                          execution.executionId
                        }
                      </td>

                      <td>
                        <span
                          className={`execution-status execution-${execution.status}`}
                        >
                          {statusLabel(
                            execution.status,
                          )}
                        </span>
                      </td>

                      <td>
                        {execution.trigger ===
                        "manual"
                          ? "يدوي"
                          : "مجدول"}
                      </td>

                      <td>
                        {
                          execution.matchedUsers
                        }
                      </td>

                      <td>
                        {
                          execution.pushSuccessCount
                        }
                      </td>

                      <td>
                        {
                          execution.pushFailureCount
                        }
                      </td>

                      <td>
                        {execution.attemptCount}
                        /
                        {execution.maxAttempts}
                      </td>

                      <td>
                        {formatTimestamp(
                          execution.startedAt ||
                            execution.queuedAt,
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedExecution && (
        <>
          <section className="notification-campaign-details-card">
            <div className="notification-campaign-details-title">
              <FontAwesomeIcon
                icon={faUsers}
              />

              <div>
                <h2>
                  المستلمون
                </h2>

                <p>
                  التنفيذ:
                  {" "}
                  {
                    selectedExecution.executionId
                  }
                </p>
              </div>
            </div>

            {reportLoading ? (
              <div className="notification-campaign-details-empty">
                جاري تحميل المستلمين...
              </div>
            ) : recipients.length ===
              0 ? (
              <div className="notification-campaign-details-empty">
                لا توجد بيانات مستلمين.
              </div>
            ) : (
              <div className="notification-details-table-wrapper">
                <table className="notification-details-table">
                  <thead>
                    <tr>
                      <th>
                        User ID
                      </th>

                      <th>
                        الحالة
                      </th>

                      <th>
                        نجاح Push
                      </th>

                      <th>
                        فشل Push
                      </th>

                      <th>
                        السبب
                      </th>

                      <th>
                        آخر تحديث
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {recipients.map(
                      recipient => (
                        <tr
                          key={
                            recipient.userId
                          }
                        >
                          <td dir="ltr">
                            {
                              recipient.userId
                            }
                          </td>

                          <td>
                            {statusLabel(
                              recipient.status,
                            )}
                          </td>

                          <td>
                            {
                              recipient.pushSuccessCount
                            }
                          </td>

                          <td>
                            {
                              recipient.pushFailureCount
                            }
                          </td>

                          <td>
                            {recipient.reason ||
                              "—"}
                          </td>

                          <td>
                            {formatTimestamp(
                              recipient.updatedAt,
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="notification-campaign-details-card">
            <div className="notification-campaign-details-title">
              <FontAwesomeIcon
                icon={faEnvelope}
              />

              <div>
                <h2>
                  Delivery Logs
                </h2>

                <p>
                  تفاصيل FCM لكل جهاز.
                </p>
              </div>
            </div>

            {reportLoading ? (
              <div className="notification-campaign-details-empty">
                جاري تحميل سجلات الإرسال...
              </div>
            ) : deliveryLogs.length ===
              0 ? (
              <div className="notification-campaign-details-empty">
                لا توجد سجلات إرسال لهذا التنفيذ.
              </div>
            ) : (
              <div className="notification-details-table-wrapper">
                <table className="notification-details-table">
                  <thead>
                    <tr>
                      <th>
                        المستخدم
                      </th>

                      <th>
                        المنصة
                      </th>

                      <th>
                        الحالة
                      </th>

                      <th>
                        Device ID
                      </th>

                      <th>
                        Message ID
                      </th>

                      <th>
                        Error
                      </th>

                      <th>
                        الوقت
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {deliveryLogs.map(
                      log => (
                        <tr
                          key={
                            log.logId
                          }
                        >
                          <td dir="ltr">
                            {
                              log.userId
                            }
                          </td>

                          <td>
                            <FontAwesomeIcon
                              icon={
                                faMobileScreen
                              }
                            />

                            {" "}
                            {log.platform ||
                              "—"}
                          </td>

                          <td>
                            {statusLabel(
                              log.status,
                            )}
                          </td>

                          <td dir="ltr">
                            {log.deviceId ||
                              "—"}
                          </td>

                          <td dir="ltr">
                            {log.messageId ||
                              "—"}
                          </td>

                          <td>
                            {log.errorCode ||
                              log.errorMessage ||
                              "—"}
                          </td>

                          <td>
                            {formatTimestamp(
                              log.createdAt,
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default NotificationCampaignDetails;